import {
  BellOutlined,
  BookOutlined,
  CloseOutlined,
  MoreOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useModel } from '@umijs/max';
import { Badge, Button, ConfigProvider, Dropdown, Grid, Tooltip } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useCallback, useEffect, useState } from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import MessageDrawer from '@/components/MessageDrawer';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { fetchDrawMessageCount } from '@/services/oms/message';
import { buildOmsMenuData, getOmsMenuSelectionState } from '@/utils/menu';
import { isSameRoutePath, normalizeRoutePath } from '@/utils/route';
import { openOmsTutorial } from '@/utils/tutorial';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

dayjs.extend(customParseFormat);

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const apiBaseUrl = process.env.UMI_APP_API_URL || '';
const loginPath = '/user/login';
const customerIdStorageKey = 'kaiya_oms_customer_id';
const uidStorageKey = 'kaiya_oms_uid';
const unameStorageKey = 'kaiya_oms_uname';

const hasValidCurrentUser = (currentUser?: any) => {
  if (!currentUser) return false;
  return Boolean(
    currentUser.customerId || currentUser.uid || currentUser.uname,
  );
};

type TimezoneClockProps = {
  fontSize?: number;
  gap?: number;
};

const TimezoneClock: React.FC<TimezoneClockProps> = ({
  fontSize = 14,
  gap = 12,
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (timeZone: string) =>
    new Intl.DateTimeFormat('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    }).format(now);

  const zones = [
    {
      label: '美东时区',
      warehouse: '纽约仓',
      tz: 'America/New_York',
      hour: -13,
    },
    {
      label: '美中时区',
      warehouse: '芝加哥仓',
      tz: 'America/Chicago',
      hour: -14,
    },
    {
      label: '美西时区',
      warehouse: '洛杉矶仓',
      tz: 'America/Los_Angeles',
      hour: -16,
    },
  ];

  return (
    <div className="header-timezones" style={{ gap, fontSize }}>
      <span className="timezones-title">世界时间：</span>
      <div className="timezones" style={{ gap }}>
        {zones.map((zone) => (
          <div key={zone.tz}>
            <span className="label" style={{ gap, paddingRight: gap }}>
              <span>
                {zone.label}({zone.hour}h)
              </span>
              <span className="tz">{formatTime(zone.tz)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TutorialEntryButton: React.FC = () => (
  <Button
    type="text"
    size="small"
    className="header-tutorial-trigger"
    icon={<BookOutlined />}
    onClick={() => openOmsTutorial()}
  >
    教程
  </Button>
);

const brandLogoTitle =
  typeof defaultSettings.title === 'string' ? defaultSettings.title : '壹起拼';

const BrandLogo: React.FC<{ collapsed?: boolean }> = ({
  collapsed = false,
}) => (
  <span
    className={`brand-logo${collapsed ? ' brand-logo-collapsed' : ''}`}
    title={brandLogoTitle}
  >
    <span className="brand-logo-mark">
      <img src="/logo2.svg" alt={collapsed ? brandLogoTitle : ''} />
    </span>
    {!collapsed && (
      <span className="brand-logo-copy">
        <span className="brand-logo-name">{brandLogoTitle}</span>
        <span className="brand-logo-submark">OMS</span>
      </span>
    )}
  </span>
);

const HeaderActions: React.FC = () => {
  const screens = Grid.useBreakpoint();
  const { initialState } = useModel('@@initialState');
  const isCompact = !screens.xxl; // xl 及以下
  const showDropdown = !screens.lg; // md 及以下折叠成 icon
  const [_aiChatOpen, _setAiChatOpen] = useState(false);

  const fontSize = isCompact ? 12 : 14;
  const gap = isCompact ? 8 : 12;
  const [messageCount, setMessageCount] = useState(0);
  const [messageTipOpen, setMessageTipOpen] = useState(true);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const customerId = String(
    (initialState?.currentUser as any)?.customerId || '',
  );
  const uid = String((initialState?.currentUser as any)?.uid || '');
  const uname = String((initialState?.currentUser as any)?.uname || '');

  const loadMessageCount = useCallback(async () => {
    const resp = await fetchDrawMessageCount();
    const payload =
      resp && typeof resp === 'object' && 'data' in resp ? resp.data : resp;
    const nextCount = Number(payload?.num || 0) || 0;
    setMessageCount(nextCount);
  }, []);

  useEffect(() => {
    loadMessageCount();
  }, [loadMessageCount]);

  const accountInfo = (
    <div
      key="account-info"
      className="header-account-info"
      style={{ display: 'flex', gap, fontSize }}
    >
      <span>我的帐户ID:{uname || '-'}</span>
      <span>邀请码: {uid || '-'}</span>
      <span>公司编码: {customerId || '-'}</span>
    </div>
  );

  const tz = <TimezoneClock key="tz" fontSize={fontSize} gap={gap} />;

  const messageBadge = (
    <Badge
      size="small"
      count={messageCount}
      offset={[-5, 6]}
      color={'#155DFB'}
      className="header-badge"
    >
      <BellOutlined
        style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 18 }}
      />
    </Badge>
  );
  const messageIcon =
    messageCount > 0 ? (
      <Tooltip
        title={
          <span className="header-message-tooltip-inner">
            <span>有一条提示消息，点击查看详情</span>
            <CloseOutlined
              className="header-message-tooltip-close"
              onClick={(event) => {
                event.stopPropagation();
                setMessageTipOpen(false);
              }}
            />
          </span>
        }
        open={messageTipOpen}
        placement="bottom"
        overlayClassName="header-message-tooltip"
      >
        <span
          className="header-message-trigger"
          onClick={() => {
            setMessageDrawerOpen(true);
            setMessageTipOpen(false);
          }}
        >
          {messageBadge}
        </span>
      </Tooltip>
    ) : (
      <span
        className="header-message-trigger"
        onClick={() => setMessageDrawerOpen(true)}
      >
        {messageBadge}
      </span>
    );

  const actionItems = showDropdown
    ? [
        <Dropdown
          key="header-dropdown"
          placement="bottomRight"
          menu={{
            items: [
              { key: 'tz', label: tz },
              { key: 'account', label: accountInfo },
            ],
          }}
        >
          <MoreOutlined style={{ fontSize: 18 }} />
        </Dropdown>,
        <span key="msg">{messageIcon}</span>,
        // <QuestionCircleOutlined key="doc" />,
        // <SelectLang key="SelectLang" />,
      ]
    : [
        tz,
        accountInfo,
        <span key="msg">{messageIcon}</span>,
        // <QuestionCircleOutlined key="doc" />,
        // <SelectLang key="SelectLang" />,
      ];

  return (
    <>
      {actionItems}
      <MessageDrawer
        open={messageDrawerOpen}
        onClose={() => setMessageDrawerOpen(false)}
        onPendingCountChange={(count) => setMessageCount(count)}
      />
    </>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      const normalizedCode =
        typeof msg?.code === 'number' ? msg.code : Number(msg?.code);
      if (normalizedCode !== 200 && normalizedCode !== 0) {
        return undefined;
      }
      const customerId = msg.data?.customerId;
      const uid = msg.data?.uid;
      const uname = msg.data?.uname;
      if (customerId) {
        localStorage.setItem(customerIdStorageKey, customerId);
      } else {
        localStorage.removeItem(customerIdStorageKey);
      }
      if (uid) {
        localStorage.setItem(uidStorageKey, uid);
      } else {
        localStorage.removeItem(uidStorageKey);
      }
      if (uname) {
        localStorage.setItem(unameStorageKey, uname);
      } else {
        localStorage.removeItem(unameStorageKey);
      }
      const currentUser = {
        name: uname || customerId || '',
        userid: customerId,
        customerId,
        uid,
        uname: uname || '',
      };
      if (!hasValidCurrentUser(currentUser)) {
        return undefined;
      }
      return currentUser;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  const currentPath = normalizeRoutePath(location.pathname);
  if (
    ![loginPath, '/user/register', '/user/register-result'].some((path) =>
      isSameRoutePath(currentPath, path),
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const menuSelectionState = getOmsMenuSelectionState(
    history.location.pathname,
  );
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>(
    menuSelectionState.openKeys ?? [],
  );

  useEffect(() => {
    if (menuSelectionState.openKeys) {
      setMenuOpenKeys(menuSelectionState.openKeys);
    }
  }, [menuSelectionState.openKeys?.join('|')]);

  const controlledMenuState =
    menuSelectionState.openKeys && menuSelectionState.selectedKeys
      ? {
          selectedKeys: menuSelectionState.selectedKeys,
          menuProps: {
            openKeys: menuOpenKeys,
            onOpenChange: setMenuOpenKeys,
          },
        }
      : {};

  return {
    headerContentRender: () => <TutorialEntryButton />,
    actionsRender: () => [<HeaderActions key="header-actions" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    menuDataRender: (menuData) =>
      buildOmsMenuData(menuData, { firstLegIcon: <RocketOutlined /> }),
    onPageChange: () => {
      const { location } = history;
      const currentPath = normalizeRoutePath(location.pathname);
      // 如果没有登录，重定向到 login
      if (
        !hasValidCurrentUser(initialState?.currentUser) &&
        !isSameRoutePath(currentPath, loginPath)
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // 统一面包屑：前置首页，其余沿用路由生成的层级
    // breadcrumbRender: (routers = []) => [
    //   { path: "/", breadcrumbName: "首页" },
    //   ...routers.filter((r) => r.path !== "/"),
    // ],
    headerTitleRender: () => <BrandLogo />,
    menuHeaderRender: (_, __, props) => {
      if (props?.layout === 'mix' && !props.isMobile) {
        return null;
      }
      return <BrandLogo collapsed={props ? Boolean(props.collapsed) : true} />;
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider componentSize="large">
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
    ...controlledMenuState,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  //  baseURL: "https://proapi.azurewebsites.net",
  // dev 环境统一走 Umi 代理，避免本地后端端口差异影响登录和 OMS 接口
  baseURL: isDev ? '/oms' : apiBaseUrl ? `${apiBaseUrl}/oms` : '/oms',
  headers: {
    'X-Tenant-Id': '1',
  },
  ...errorConfig,
};
